"use client";

import { medimoQC } from "@medimo/core-api/internal/next";
import { useGlobalTranscriptionStatus } from "@medimo/global-state";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { flushPendingClosedEvent } from "../lib/closed-event";
import { NotificationDialog } from "./dialog";
import {
  type ReleaseNotification,
  ReleaseNotificationDialog,
} from "./release-dialog";

const LOGIN_ATTEMPT_ID_KEY = "releaseNotificationLoginAttemptId";
// claim の試行回数（初回＋再試行2回）
const CLAIM_ATTEMPTS = [0, 1, 2] as const;

type ClaimState = "idle" | "pending" | "resolved" | "error";

// ログイン時通知の単一コーディネータ。
// ログインフラグ（showNotificationDialog）の消費、リリース通知モーダルの claim、
// 既存お知らせダイアログとの優先制御をここに集約する。
// レスポンシブ分岐の外に 1 箇所だけマウントすること（二重マウート禁止:
// claim が二重発行され表示回数を余分に消費する）
export const NotificationCoordinator = () => {
  const pathname = usePathname();
  const transcribingStatus = useGlobalTranscriptionStatus(
    (state) => state.status,
  );
  const [isJustLoggedIn, setIsJustLoggedIn] = useState(false);
  const [loginAttemptId, setLoginAttemptId] = useState<string | undefined>(
    undefined,
  );
  const [claimState, setClaimState] = useState<ClaimState>("idle");
  const [releaseNotification, setReleaseNotification] =
    useState<ReleaseNotification | null>(null);

  // 共通の QueryClient は mutation を自動で再試行するため、ここでは無効化して
  // 下の試行回数だけが実リクエスト回数になるようにする
  const { mutateAsync: claim } = medimoQC.useMutation(
    "post",
    "/v2/notifications/popup/claim",
    { retry: false },
  );

  // ログインフラグを一度だけ読み取り・削除する（sign-in / two-factor /
  // set-initial-password が設定する既存フラグをそのまま利用）
  useEffect(() => {
    // 前回の起動で届かなかった closed イベントをここで拾い直す
    flushPendingClosedEvent();
    try {
      const showDialog = sessionStorage.getItem("showNotificationDialog");
      if (showDialog === "true") {
        setIsJustLoggedIn(true);
        // claim・closed のリトライに備えてログイン試行IDを保持し、
        // 次のログインでは新しいIDに置き換える
        const attemptId = crypto.randomUUID();
        sessionStorage.setItem(LOGIN_ATTEMPT_ID_KEY, attemptId);
        setLoginAttemptId(attemptId);
      }
      // お知らせの有無に関わらず、フラグは削除する
      sessionStorage.removeItem("showNotificationDialog");
    } catch (e) {
      console.error("セッションストレージの読み込みに失敗しました:", e);
    }
  }, []);

  // 表示条件（/consults・書き起こし非実行中）が揃った時点で claim する
  useEffect(() => {
    if (claimState !== "idle") return;
    if (!(isJustLoggedIn && loginAttemptId)) return;
    if (pathname !== "/consults") return;
    if (transcribingStatus !== "inactive") return;

    setClaimState("pending");
    (async () => {
      // 通信エラーは同じ login_attempt_id で再試行する（サーバー側で冪等）
      for (const attempt of CLAIM_ATTEMPTS) {
        try {
          const result = await claim({
            body: { login_attempt_id: loginAttemptId },
          });
          setReleaseNotification(result.notification ?? null);
          setClaimState("resolved");
          return;
        } catch (e) {
          console.error(
            `リリース通知の取得に失敗しました (${attempt + 1}/${CLAIM_ATTEMPTS.length}):`,
            e,
          );
        }
      }
      // 最終的に取得できない場合は既存ダイアログへフォールバックする
      // （claim は未消費のため、リリース通知は次回ログインで再試行される）
      setClaimState("error");
    })();
  }, [
    claimState,
    isJustLoggedIn,
    loginAttemptId,
    pathname,
    transcribingStatus,
    claim,
  ]);

  const showReleaseDialog =
    claimState === "resolved" && releaseNotification !== null;
  // リリース通知がある場合は優先し、既存お知らせダイアログは次回ログインに回す
  // （既読はお知らせIDごとにサーバーが記録するため、ローカルの境界日時を進める仕組みはない）
  const legacyDialogJustLoggedIn =
    isJustLoggedIn &&
    (claimState === "error" ||
      (claimState === "resolved" && releaseNotification === null));

  return (
    <>
      {showReleaseDialog && releaseNotification && loginAttemptId ? (
        <ReleaseNotificationDialog
          notification={releaseNotification}
          loginAttemptId={loginAttemptId}
        />
      ) : null}
      <NotificationDialog isJustLoggedIn={legacyDialogJustLoggedIn} />
    </>
  );
};
